// frontend/src/components/Admin/UserAdmin.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Table,
  Tag,
  Switch,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Card,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import {
  UserAddOutlined,
  DeleteOutlined,
  KeyOutlined,
  LogoutOutlined,
  MessageOutlined,
  FilterOutlined,
  ReloadOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/api';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const ROLE_COLORS = {
  admin: 'volcano',
  kostenplaner: 'green',
  benutzer: 'blue',
  gast: 'default',
};

const PERMISSION_LABELS = {
  suche: 'Klassische Suche',
  ki_suche: 'KI-Suche',
  kostenschaetzung: 'Kostenschätzung',
  benutzerverwaltung: 'Benutzerverwaltung',
  datenimport: 'Datenimport',
};

/** Fehlermeldung aus der API (deutsch) oder Fallback anzeigen. */
const apiError = (error, fallback) =>
  message.error(error.response?.data?.detail || fallback);

const UserAdmin = () => {
  const { user: currentUser, logout, hasPermission } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [savingPassword, setSavingPassword] = useState(false);

  const [createForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [usersResponse, rolesResponse] = await Promise.all([
        adminAPI.listUsers(),
        adminAPI.getRoles(),
      ]);
      setUsers(usersResponse.data.users || []);
      setRoles(rolesResponse.data.roles || []);
    } catch (error) {
      apiError(error, 'Benutzerliste konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleRoleChange = async (username, role) => {
    try {
      await adminAPI.updateUser(username, { role });
      message.success(`Rolle von '${username}' geändert`);
      loadAll();
    } catch (error) {
      apiError(error, 'Rolle konnte nicht geändert werden');
    }
  };

  const handleActiveChange = async (username, isActive) => {
    try {
      await adminAPI.updateUser(username, { is_active: isActive });
      message.success(
        isActive
          ? `Benutzer '${username}' aktiviert`
          : `Benutzer '${username}' deaktiviert`
      );
      loadAll();
    } catch (error) {
      apiError(error, 'Status konnte nicht geändert werden');
    }
  };

  const handleDelete = async (username) => {
    try {
      await adminAPI.deleteUser(username);
      message.success(`Benutzer '${username}' gelöscht`);
      loadAll();
    } catch (error) {
      apiError(error, 'Benutzer konnte nicht gelöscht werden');
    }
  };

  const handleCreate = async (values) => {
    setCreating(true);
    try {
      await adminAPI.createUser({
        username: values.username.trim(),
        email: values.email.trim(),
        password: values.password,
        full_name: values.full_name?.trim() || null,
        role: values.role,
      });
      message.success(`Benutzer '${values.username.trim()}' angelegt`);
      setCreateOpen(false);
      createForm.resetFields();
      loadAll();
    } catch (error) {
      apiError(error, 'Benutzer konnte nicht angelegt werden');
    } finally {
      setCreating(false);
    }
  };

  const handlePasswordSave = async (values) => {
    setSavingPassword(true);
    try {
      await adminAPI.updateUser(passwordUser, { password: values.password });
      message.success(`Passwort für '${passwordUser}' geändert`);
      setPasswordUser(null);
      passwordForm.resetFields();
    } catch (error) {
      apiError(error, 'Passwort konnte nicht geändert werden');
    } finally {
      setSavingPassword(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value.replace(' ', 'T'));
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('de-CH', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const roleOptions = roles.map((role) => ({
    value: role.role,
    label: role.label,
  }));

  const columns = [
    {
      title: 'Benutzername',
      dataIndex: 'username',
      key: 'username',
      sorter: (a, b) => a.username.localeCompare(b.username),
      render: (username) => (
        <Space>
          <Text strong>{username}</Text>
          {username === currentUser?.username && <Tag color="purple">Sie</Tag>}
        </Space>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (fullName) => fullName || '—',
    },
    {
      title: 'E-Mail',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Rolle',
      dataIndex: 'role',
      key: 'role',
      filters: roles.map((role) => ({ text: role.label, value: role.role })),
      onFilter: (value, record) => record.role === value,
      render: (role, record) => (
        <Select
          value={role}
          options={roleOptions}
          style={{ width: 150 }}
          size="small"
          onChange={(newRole) => handleRoleChange(record.username, newRole)}
        />
      ),
    },
    {
      title: 'Aktiv',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive, record) => (
        <Tooltip
          title={
            record.username === currentUser?.username
              ? 'Das eigene Konto kann nicht deaktiviert werden'
              : undefined
          }
        >
          <Switch
            checked={isActive}
            size="small"
            disabled={record.username === currentUser?.username}
            onChange={(checked) => handleActiveChange(record.username, checked)}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Letzte Anmeldung',
      dataIndex: 'last_login',
      key: 'last_login',
      render: formatDate,
    },
    {
      title: 'Aktionen',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Passwort ändern">
            <Button
              size="small"
              icon={<KeyOutlined />}
              onClick={() => setPasswordUser(record.username)}
            />
          </Tooltip>
          <Popconfirm
            title={`Benutzer '${record.username}' löschen?`}
            description="Dieser Schritt kann nicht rückgängig gemacht werden."
            okText="Löschen"
            okButtonProps={{ danger: true }}
            cancelText="Abbrechen"
            onConfirm={() => handleDelete(record.username)}
            disabled={record.username === currentUser?.username}
          >
            <Tooltip
              title={
                record.username === currentUser?.username
                  ? 'Das eigene Konto kann nicht gelöscht werden'
                  : 'Benutzer löschen'
              }
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={record.username === currentUser?.username}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      <Header
        style={{
          background: '#fff',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <img
          src="/logo_black.png"
          alt="PERITA"
          style={{ height: '50px', margin: '7px 0' }}
        />
        <Space>
          {hasPermission('ki_suche') && (
            <Button icon={<MessageOutlined />} onClick={() => navigate('/')}>
              KI-Suche
            </Button>
          )}
          <Button icon={<FilterOutlined />} onClick={() => navigate('/search')}>
            Klassische Suche
          </Button>
          <Text type="secondary">
            {currentUser?.username} ({currentUser?.role_label})
          </Text>
          <Button type="text" icon={<LogoutOutlined />} onClick={handleLogout}>
            Abmelden
          </Button>
        </Space>
      </Header>

      <Content style={{ margin: '24px auto', maxWidth: 1200, width: '100%', padding: '0 16px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
              }}
            >
              <Title level={3} style={{ margin: 0 }}>
                <TeamOutlined /> Benutzerverwaltung
              </Title>
              <Space>
                <Button icon={<ReloadOutlined />} onClick={loadAll} loading={loading}>
                  Aktualisieren
                </Button>
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={() => setCreateOpen(true)}
                >
                  Neuer Benutzer
                </Button>
              </Space>
            </div>

            <Table
              dataSource={users}
              columns={columns}
              rowKey="username"
              loading={loading}
              pagination={{ pageSize: 15, hideOnSinglePage: true }}
              size="middle"
            />
          </Card>

          <Card title="Rollen und ihre Berechtigungen" size="small">
            <Table
              dataSource={roles}
              rowKey="role"
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Rolle',
                  dataIndex: 'label',
                  key: 'label',
                  width: 160,
                  render: (label, record) => (
                    <Tag color={ROLE_COLORS[record.role] || 'default'}>{label}</Tag>
                  ),
                },
                {
                  title: 'Beschreibung',
                  dataIndex: 'description',
                  key: 'description',
                },
                {
                  title: 'Zugriff auf',
                  dataIndex: 'permissions',
                  key: 'permissions',
                  render: (permissions) => (
                    <>
                      {permissions.map((permission) => (
                        <Tag key={permission}>
                          {PERMISSION_LABELS[permission] || permission}
                        </Tag>
                      ))}
                    </>
                  ),
                },
              ]}
            />
          </Card>
        </Space>
      </Content>

      {/* Neuen Benutzer anlegen */}
      <Modal
        title="Neuen Benutzer anlegen"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        okText="Anlegen"
        cancelText="Abbrechen"
        confirmLoading={creating}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreate}
          initialValues={{ role: 'benutzer' }}
        >
          <Form.Item
            name="username"
            label="Benutzername"
            rules={[
              { required: true, message: 'Bitte Benutzername eingeben' },
              {
                pattern: /^[a-zA-Z0-9_.-]{2,32}$/,
                message: '2–32 Zeichen, nur Buchstaben, Zahlen, . _ -',
              },
            ]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item name="full_name" label="Vollständiger Name">
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="email"
            label="E-Mail"
            rules={[
              { required: true, message: 'Bitte E-Mail-Adresse eingeben' },
              { type: 'email', message: 'Ungültige E-Mail-Adresse' },
            ]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="role"
            label="Rolle"
            rules={[{ required: true, message: 'Bitte Rolle wählen' }]}
          >
            <Select
              options={roles.map((role) => ({
                value: role.role,
                label: `${role.label} — ${role.description}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="password"
            label="Passwort"
            rules={[
              { required: true, message: 'Bitte Passwort eingeben' },
              { min: 8, message: 'Mindestens 8 Zeichen' },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="password_confirm"
            label="Passwort bestätigen"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Bitte Passwort bestätigen' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error('Die Passwörter stimmen nicht überein')
                  );
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Passwort ändern */}
      <Modal
        title={`Passwort ändern: ${passwordUser || ''}`}
        open={Boolean(passwordUser)}
        onCancel={() => {
          setPasswordUser(null);
          passwordForm.resetFields();
        }}
        onOk={() => passwordForm.submit()}
        okText="Speichern"
        cancelText="Abbrechen"
        confirmLoading={savingPassword}
        destroyOnClose
      >
        <Form form={passwordForm} layout="vertical" onFinish={handlePasswordSave}>
          <Form.Item
            name="password"
            label="Neues Passwort"
            rules={[
              { required: true, message: 'Bitte Passwort eingeben' },
              { min: 8, message: 'Mindestens 8 Zeichen' },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="password_confirm"
            label="Passwort bestätigen"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Bitte Passwort bestätigen' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error('Die Passwörter stimmen nicht überein')
                  );
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default UserAdmin;
